
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

const KraaifonteinPage: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for Kraaifontein page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Kraaifontein Painters",
      "description": "Professional painting contractors in Kraaifontein, Western Cape. Expert interior, exterior & roof painting services for residential and commercial properties.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/kraaifontein-painters`,
      "telephone": "",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Kraaifontein",
        "addressRegion": "Western Cape",
        "addressCountry": "ZA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-33.8569",
        "longitude": "18.7089"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "-33.8569",
          "longitude": "18.7089"
        },
        "geoRadius": "5000"
      },
      "services": [
        "Interior Painting Kraaifontein",
        "Exterior Painting Kraaifontein", 
        "Roof Painting Kraaifontein",
        "Commercial Painting Kraaifontein",
        "Residential Painting Kraaifontein"
      ],
      "areaServed": "Kraaifontein, Western Cape, South Africa"
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Kraaifontein Painters - Professional Painting Contractors Western Cape | Get Quote";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting contractors in Kraaifontein, Western Cape. Expert interior, exterior & roof painting services. Quality workmanship for residential and commercial properties. Get your free quote today.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Kraaifontein painters, painting contractors Kraaifontein, house painters Western Cape, interior painting, exterior painting');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Interior Painting',
      description: 'Transform your Kraaifontein home with professional interior painting services that enhance every room.',
      link: '/interior-painting'
    },
    {
      icon: 'ri-building-line',
      title: 'Exterior Painting',
      description: 'Protect your Kraaifontein property with weather-resistant exterior painting solutions.',
      link: '/exterior-painting'
    },
    {
      icon: 'ri-home-2-line',
      title: 'Roof Painting',
      description: 'Extend your roof\'s lifespan with professional roof painting services in Kraaifontein.',
      link: '/roof-painting'
    },
    {
      icon: 'ri-office-building-line',
      title: 'Commercial Painting',
      description: 'Professional commercial painting services for Kraaifontein businesses and offices.',
      link: '/commercial-painting'
    },
  ];

  const whyChooseUs = [
    {
      icon: 'ri-map-pin-line',
      title: 'Local Kraaifontein Expertise',
      description: 'Our contractors understand Kraaifontein\'s unique climate and architectural styles perfectly.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Guaranteed',
      description: 'All Kraaifontein painting projects come with comprehensive quality guarantees and warranties.',
    },
    {
      icon: 'ri-time-line',
      title: 'Fast Response',
      description: 'Quick response times for all Kraaifontein painting quotes and emergency touch-ups.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Competitive Pricing',
      description: 'Best value painting services in Kraaifontein with transparent, upfront pricing.',
    },
  ];

  const paintingProcess = [
    {
      step: '1',
      title: 'Kraaifontein Property Assessment',
      description: 'Thorough evaluation of your Kraaifontein property\'s painting needs and surface conditions.'
    },
    {
      step: '2',
      title: 'Custom Kraaifontein Quote',
      description: 'Detailed quote tailored specifically for your Kraaifontein painting project requirements.'
    },
    {
      step: '3',
      title: 'Professional Preparation',
      description: 'Comprehensive surface preparation using techniques suited to Kraaifontein\'s climate.'
    },
    {
      step: '4',
      title: 'Expert Application',
      description: 'Professional painting application by experienced Kraaifontein painting contractors.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/877358d3d73879d55bf46478a719fedf.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
            <span className="block">Kraaifontein</span>
            <span className="block text-[#e9f638]">PAINTERS</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Professional painting contractors serving Kraaifontein and surrounding areas.
          </p>
          <p className="text-lg md:text-xl mb-12 max-w-4xl mx-auto opacity-90">
            Connect with Kraaifontein's most trusted painting professionals. We deliver exceptional 
            workmanship for residential and commercial properties throughout the area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="text-black px-8 py-4 rounded-full text-lg font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#e9f638' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d4e532'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e9f638'}
            >
              Get Free Kraaifontein Quote
            </Link>
            <a 
              href="#services" 
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-black transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              View Services
            </a>
          </div>
        </div>
      </section>

      {/* About Kraaifontein Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Expert Painting Services in Kraaifontein
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Kraaifontein, known for its growing residential communities and beautiful mountain backdrop, 
                requires painting services that can withstand the Western Cape's diverse weather conditions. 
                Our network of professional painting contractors specializes in delivering high-quality 
                results that enhance and protect Kraaifontein properties.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                From the established neighborhoods near Kraaifontein Station to the newer developments 
                spreading toward the Boland Mountains, our Kraaifontein painting contractors understand 
                the unique architectural styles and environmental challenges of the area. We provide 
                comprehensive painting solutions for both residential and commercial properties.
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Kraaifontein's Growing Community and Painting Needs
              </h3>
              <p className="text-lg text-gray-600">
                As Kraaifontein continues to expand with new housing developments and commercial centers, 
                the demand for professional painting services grows. Our contractors are equipped to 
                handle everything from new construction painting to heritage home restoration, ensuring 
                every Kraaifontein property receives the attention it deserves.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Beautiful%20residential%20neighborhood%20in%20Kraaifontein%20South%20Africa%20with%20mountain%20backdrop%2C%20modern%20suburban%20homes%20with%20well-maintained%20exteriors%2C%20professional%20painting%20work%20visible%2C%20clean%20streets%20and%20landscaping%2C%20bright%20daylight%20photography&width=600&height=400&seq=kraaifontein-neighborhood&orientation=landscape"
                alt="Kraaifontein residential area with professional painting services"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Painting Services in Kraaifontein
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our Kraaifontein painting contractors provide complete painting solutions for all property types
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Link 
                key={index} 
                to={service.link}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 border border-gray-100 group cursor-pointer"
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                  <i className={`${service.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-[#e9f638] transition-colors duration-200">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Kraaifontein Specific Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Specialized Kraaifontein Painting Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored painting services designed specifically for Kraaifontein's unique environment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Kraaifontein New Development Painting
              </h4>
              <p className="text-gray-600 mb-4">
                Specialized painting services for Kraaifontein's rapidly growing residential developments. 
                We work with builders and homeowners to ensure new properties receive the highest quality 
                paint finishes that will last for years.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  New construction painting
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Builder partnerships
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Quality guarantees
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Kraaifontein Weather-Resistant Coatings
              </h4>
              <p className="text-gray-600 mb-4">
                Kraaifontein's location near the mountains means exposure to varying weather conditions. 
                Our contractors use specialized weather-resistant paints and coatings that protect your 
                property from wind, rain, and UV damage.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  UV-resistant formulations
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Wind and rain protection
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Long-lasting durability
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Kraaifontein Commercial Property Painting
              </h4>
              <p className="text-gray-600 mb-4">
                From shopping centers to office buildings, Kraaifontein's commercial properties require 
                professional painting services that minimize business disruption while delivering 
                exceptional results that attract customers and tenants.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Flexible scheduling
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Minimal disruption
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Professional results
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Kraaifontein Painting Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Local expertise combined with professional excellence for all Kraaifontein painting projects
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                  <i className={`${item.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Kraaifontein Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional painting process designed specifically for Kraaifontein properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                  <span className="text-2xl font-bold text-black">{process.step}</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">{process.title}</h4>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kraaifontein Areas Covered */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Kraaifontein Areas We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive painting services throughout Kraaifontein and surrounding neighborhoods
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Central Kraaifontein</h5>
              <p className="text-gray-600">
                Established neighborhoods around Kraaifontein Station and the main commercial areas, 
                including older homes that require specialized restoration painting techniques.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Kraaifontein Heights</h5>
              <p className="text-gray-600">
                Newer residential developments with modern homes that benefit from contemporary 
                painting techniques and color schemes suited to the mountain views.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Kraaifontein Industrial</h5>
              <p className="text-gray-600">
                Commercial and industrial properties requiring specialized coatings and painting 
                systems designed for durability and professional appearance.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Kraaifontein East</h5>
              <p className="text-gray-600">
                Growing residential areas with mix of established and new properties, each requiring 
                tailored painting approaches based on age and architectural style.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Kraaifontein West</h5>
              <p className="text-gray-600">
                Developing areas with new construction projects and established communities, 
                offering opportunities for both new build and renovation painting services.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h6 className="text-lg font-semibold text-gray-900 mb-3">Surrounding Kraaifontein Areas</h6>
              <p className="text-gray-600">
                Extended service area including nearby developments and rural properties that 
                require specialized painting solutions for unique environmental conditions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Related Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our complete range of professional painting services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link 
              to="/interior-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Interior Painting
              </h3>
            </Link>

            <Link 
              to="/exterior-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Exterior Painting
              </h3>
            </Link>

            <Link 
              to="/roof-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-2-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Roof Painting
              </h3>
            </Link>

            <Link 
              to="/commercial-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-office-building-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Commercial Painting
              </h3>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Kraaifontein Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied customers throughout Kraaifontein and surrounding areas
            </p>
          </div>
          <TestimonialsSlider />
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Your Free Kraaifontein Painting Quote
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to transform your Kraaifontein property? Connect with professional painting contractors today
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      <Footer />
      
      {/* Elfsight AI Chatbot */}
      <div>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>
      </div>
    </div>
  );
};

export default KraaifonteinPage;
